import api from './api'
import type {
  Agent,
  AgentListResponse,
  AgentListParams,
  LLMConfig,
  MemoryConfig,
  AgentSettings,
} from '@/schemas/agent'
import type {
  PlaygroundMessage,
  PlaygroundResponse,
} from '@/schemas/playground'
import type { AxiosRequestConfig } from 'axios'

// Request types for agent operations
export interface AgentCreateRequest {
  name: string
  description?: string | null
  system_prompt?: string | null
  llm_config?: LLMConfig
  memory_config?: MemoryConfig
  settings?: AgentSettings
  is_template?: boolean
  is_active?: boolean
}

export interface AgentUpdateRequest {
  name?: string
  description?: string | null
  system_prompt?: string | null
  llm_config?: LLMConfig
  memory_config?: MemoryConfig
  settings?: AgentSettings
  is_template?: boolean
  is_active?: boolean
}

export interface AgentDuplicateRequest {
  name: string
  description?: string | null
}

export interface AgentToolAttachRequest {
  tool_id: string
  config?: Record<string, unknown>
}

export interface AgentToolUpdateRequest {
  config: Record<string, unknown>
}

export interface AgentKnowledgeBaseAttachRequest {
  knowledge_base_id: string
}

// Response types
export interface AgentCreateResponse {
  message: string
  agent: Agent
}

export interface AgentUpdateResponse {
  message: string
  agent: Agent
}

export interface AgentDeleteResponse {
  message: string
}

export interface AgentDuplicateResponse {
  message: string
  agent: Agent
}

export interface AgentToolResponse {
  message: string
  agent_tool: {
    id: string
    tool_id: string
    tool_name: string
    config: Record<string, unknown>
  }
}

export interface AgentKnowledgeBaseResponse {
  message: string
  agent_knowledge_base: {
    id: string
    knowledge_base_id: string
    knowledge_base_name: string
  }
}

// History types
export interface AgentChangeHistoryItem {
  id: string
  field_name: string
  old_value: string | null
  new_value: string | null
  changed_by: string
  changed_by_name: string | null
  changed_by_email: string | null
  created_at: string
}

export interface AgentChangeHistoryResponse {
  history: AgentChangeHistoryItem[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

// Memory config types
export interface AgentMemoryUpdateRequest {
  enabled?: boolean
  type?: string
  scope?: string
  context_window?: number
  retention_days?: number | null
  auto_summarize?: boolean
}

export interface AgentMemoryClearResponse {
  message: string
  agent_id: string
}

export const agentService = {
  // List agents with pagination and filters
  async listAgents(
    projectId: string,
    params?: AgentListParams,
    config?: AxiosRequestConfig
  ): Promise<AgentListResponse> {
    const response = await api.get<AgentListResponse>(
      `/projects/${projectId}/agents`,
      { params, ...config }
    )
    return response.data
  },

  // Get a single agent by ID
  async getAgent(
    projectId: string,
    agentId: string,
    config?: AxiosRequestConfig
  ): Promise<Agent> {
    const response = await api.get<Agent>(
      `/projects/${projectId}/agents/${agentId}`,
      config
    )
    return response.data
  },

  // Create a new agent
  async createAgent(
    projectId: string,
    data: AgentCreateRequest,
    config?: AxiosRequestConfig
  ): Promise<AgentCreateResponse> {
    const response = await api.post<AgentCreateResponse>(
      `/projects/${projectId}/agents`,
      data,
      config
    )
    return response.data
  },

  // Update an existing agent
  async updateAgent(
    projectId: string,
    agentId: string,
    data: AgentUpdateRequest,
    config?: AxiosRequestConfig
  ): Promise<AgentUpdateResponse> {
    const response = await api.patch<AgentUpdateResponse>(
      `/projects/${projectId}/agents/${agentId}`,
      data,
      config
    )
    return response.data
  },

  // Delete an agent (soft delete)
  async deleteAgent(
    projectId: string,
    agentId: string,
    config?: AxiosRequestConfig
  ): Promise<AgentDeleteResponse> {
    const response = await api.delete<AgentDeleteResponse>(
      `/projects/${projectId}/agents/${agentId}`,
      config
    )
    return response.data
  },

  // Duplicate an agent
  async duplicateAgent(
    projectId: string,
    agentId: string,
    data: AgentDuplicateRequest,
    config?: AxiosRequestConfig
  ): Promise<AgentDuplicateResponse> {
    const response = await api.post<AgentDuplicateResponse>(
      `/projects/${projectId}/agents/${agentId}/duplicate`,
      data,
      config
    )
    return response.data
  },

  // Attach a tool to an agent
  async attachTool(
    projectId: string,
    agentId: string,
    data: AgentToolAttachRequest,
    config?: AxiosRequestConfig
  ): Promise<AgentToolResponse> {
    const response = await api.post<AgentToolResponse>(
      `/projects/${projectId}/agents/${agentId}/tools`,
      data,
      config
    )
    return response.data
  },

  // Detach a tool from an agent
  async detachTool(
    projectId: string,
    agentId: string,
    toolId: string,
    config?: AxiosRequestConfig
  ): Promise<AgentDeleteResponse> {
    const response = await api.delete<AgentDeleteResponse>(
      `/projects/${projectId}/agents/${agentId}/tools/${toolId}`,
      config
    )
    return response.data
  },

  // Update tool configuration
  async updateToolConfig(
    projectId: string,
    agentId: string,
    toolId: string,
    data: AgentToolUpdateRequest,
    config?: AxiosRequestConfig
  ): Promise<AgentToolResponse> {
    const response = await api.patch<AgentToolResponse>(
      `/projects/${projectId}/agents/${agentId}/tools/${toolId}`,
      data,
      config
    )
    return response.data
  },

  // Attach a knowledge base to an agent
  async attachKnowledgeBase(
    projectId: string,
    agentId: string,
    data: AgentKnowledgeBaseAttachRequest,
    config?: AxiosRequestConfig
  ): Promise<AgentKnowledgeBaseResponse> {
    const response = await api.post<AgentKnowledgeBaseResponse>(
      `/projects/${projectId}/agents/${agentId}/knowledge-bases`,
      data,
      config
    )
    return response.data
  },

  // Detach a knowledge base from an agent
  async detachKnowledgeBase(
    projectId: string,
    agentId: string,
    knowledgeBaseId: string,
    config?: AxiosRequestConfig
  ): Promise<AgentDeleteResponse> {
    const response = await api.delete<AgentDeleteResponse>(
      `/projects/${projectId}/agents/${agentId}/knowledge-bases/${knowledgeBaseId}`,
      config
    )
    return response.data
  },

  // Get agent change history
  async getAgentHistory(
    projectId: string,
    agentId: string,
    params?: { page?: number; page_size?: number },
    config?: AxiosRequestConfig
  ): Promise<AgentChangeHistoryResponse> {
    const response = await api.get<AgentChangeHistoryResponse>(
      `/projects/${projectId}/agents/${agentId}/history`,
      { params, ...config }
    )
    return response.data
  },

  // Update agent memory configuration
  async updateMemoryConfig(
    projectId: string,
    agentId: string,
    data: AgentMemoryUpdateRequest,
    config?: AxiosRequestConfig
  ): Promise<Agent> {
    const response = await api.patch<Agent>(
      `/projects/${projectId}/agents/${agentId}/memory`,
      data,
      config
    )
    return response.data
  },

  // Clear agent memory
  async clearMemory(
    projectId: string,
    agentId: string,
    config?: AxiosRequestConfig
  ): Promise<AgentMemoryClearResponse> {
    const response = await api.delete<AgentMemoryClearResponse>(
      `/projects/${projectId}/agents/${agentId}/memory`,
      config
    )
    return response.data
  },

  // Test agent in playground
  async testAgent(
    projectId: string,
    agentId: string,
    data: { message: string; history?: PlaygroundMessage[] },
    config?: AxiosRequestConfig
  ): Promise<PlaygroundResponse> {
    const response = await api.post<PlaygroundResponse>(
      `/projects/${projectId}/agents/${agentId}/playground`,
      data,
      config
    )
    return response.data
  },
}
